import {
  Box,
  FormControl,
  FormControlLabel,
  IconButton,
  Link,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Tooltip,
  Divider,
} from "@mui/material";
import { Info } from "@mui/icons-material";
import { StepLayoutContainer } from "components/Layout/StepLayoutContainer";
import { BackgroundTypography } from "components/Text";
import {
  BaseSyntheticEvent,
  ChangeEvent,
  FC,
  useState,
  useEffect,
  useMemo,
} from "react";
import { ACTIONS, STEPS } from "../config";
import { SuccessRubric } from "modals/SuccessRubric";
import { RateSelector } from "../Components/RateSelector";
import { ErrorType, RequestFeedbackData } from "../types";
import { RequestFeedbackSteps } from "helpers/constants";
import { scrollToElementFromBottom } from "utils/scroll";
import { useVoiceFieldFocus } from "hooks/useVoiceFieldFocus";
import { CustomLabel } from "types/CustomLabel";
import { REQUIREMENT } from "helpers/constants";
import { SubRequirementsModal } from "components/Feedback/SubRequirementsModal";
import { FeedbackType } from "types/feedback";
import { Requirement } from "types/requirement";
import { getRequirementDisplayData } from "utils/feedbackIter";

type ReflectProps = {
  onUpdate: (data: any) => void;
  data: RequestFeedbackData;
  handleStep: (step: string, action?: string) => void;
  error: ErrorType;
  customLabels: CustomLabel[];
  historicalFeedbacks?: FeedbackType[];
  allRequirements?: Requirement[];
};

export const Reflect1: FC<ReflectProps> = ({
  onUpdate,
  data,
  handleStep,
  error,
  customLabels,
  historicalFeedbacks = [],
  allRequirements = [],
}) => {
  const [openSuccessRubric, setOpenSuccessRubric] = useState(false);
  const [selectedCompetencyForRubric, setSelectedCompetencyForRubric] = useState<any>(null);
  const [openSubRequirementsModal, setOpenSubRequirementsModal] = useState(false);
  const { activeFieldId } = useVoiceFieldFocus();
  const coachAvailableActive = activeFieldId === "coachAvailable";

  const isITER = useMemo(() => {
    return data.selectedCompetencySkill?.type === REQUIREMENT.REQUIREMENT_ITER;
  }, [data.selectedCompetencySkill]);

  // Build ratings by requirement for ITER competencies
  const buildRatingsByRequirementForIter = useMemo(() => {
    if (!isITER || !data.selectedCompetencySkill?.name) {
      return {};
    }

    const ratingsByRequirement: Record<
      string,
      {
        date: string;
        learnerRating?: number | null;
        coachRating?: number | null;
      }[]
    > = {};

    const iterName = data.selectedCompetencySkill.name;
    const history = historicalFeedbacks
      .filter((fb) => {
        const requirementData = getRequirementDisplayData(fb, allRequirements);
        return requirementData.isITER && requirementData.name === iterName;
      })
      .sort((a, b) => {
        const da = a.observation_date || a.created_at || "";
        const db = b.observation_date || b.created_at || "";
        return da.localeCompare(db);
      });

    history.forEach((fb) => {
      const date = (fb.observation_date || fb.created_at) as string;
      fb.feedback_skills.forEach((skill) => {
        const reqId = skill.requirement?.requirement_id;
        if (!reqId) return;
        if (!ratingsByRequirement[reqId]) {
          ratingsByRequirement[reqId] = [];
        }
        ratingsByRequirement[reqId].push({
          date,
          learnerRating: skill.self_rating ?? null,
          coachRating: skill.coach_rating ?? null,
        });
      });
    });

    return ratingsByRequirement;
  }, [isITER, data.selectedCompetencySkill, historicalFeedbacks, allRequirements]);

  const subRequirements = useMemo(() => {
    if (!isITER || !data.selectedCompetencySkill?.competencies) {
      return [];
    }
    return data.selectedCompetencySkill.competencies
      .map((c: any) => c.competency)
      .filter(Boolean);
  }, [isITER, data.selectedCompetencySkill]);

  // Scroll to first missing ITER field (comment or rating) when errors appear
  useEffect(() => {
    if (isITER) {
      // Check for comment errors first
      if (error.iterSubRequirementCommentErrors) {
        const firstMissingCommentId = Object.keys(error.iterSubRequirementCommentErrors).find(
          (requirementId) => error.iterSubRequirementCommentErrors![requirementId] !== null
        );
        if (firstMissingCommentId) {
          scrollToElementFromBottom(`iter-comment-${firstMissingCommentId}`);
          return;
        }
      }
      // Then check for rating errors
      if (error.iterSubRequirementRateErrors) {
        const firstMissingRatingId = Object.keys(error.iterSubRequirementRateErrors).find(
          (requirementId) => error.iterSubRequirementRateErrors![requirementId] !== null
        );
        if (firstMissingRatingId) {
          scrollToElementFromBottom(`iter-rating-${firstMissingRatingId}`);
          return;
        }
      }
    } else {
      // For non-ITER, scroll to perform field or rate selector
      if (error.perform) {
        scrollToElementFromBottom("perform-text-field");
      } else if (error.rate) {
        scrollToElementFromBottom("rate-selector");
      }
    }
  }, [error.iterSubRequirementCommentErrors, error.iterSubRequirementRateErrors, error.perform, error.rate, isITER]);

  return (
    <StepLayoutContainer
      title="Reflect on Your Performance & Ask for Feedback"
      onClickBack={() => handleStep(STEPS.PERFORM, "back")}
      onClickNext={() =>
        data.coachAvailable
          ? handleStep(STEPS.REFLECT_COACH)
          : handleStep(STEPS.SUBMIT)
      }
      marginXSBottom={19}
    >
      <Box my={2}>
        {!isITER && (
          <Typography>
            Need a reminder? Review the goals and success rubric of the
            requirement you selected to perform.{" "}
            <Link
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setOpenSuccessRubric(true);
              }}
            >
              See Success Rubric
            </Link>
          </Typography>
        )}

        <BackgroundTypography text="Personal Reflection" />

        {isITER && subRequirements.length > 0 ? (
          // For ITER: Group both questions per competency
          <Box my={2}>
            {subRequirements.map((subReq: any, index: number) => {
              const commentError = error.iterSubRequirementCommentErrors?.[subReq.requirement_id];
              const ratingError = error.iterSubRequirementRateErrors?.[subReq.requirement_id];
              return (
                <Box
                  key={subReq.requirement_id}
                  sx={{ 
                    mb: 3,
                    borderTop: index > 0 ? "1px solid rgba(0,0,0,0.2)" : "none",
                    pt: index > 0 ? 3 : 0,
                    mt: index > 0 ? 3 : 0
                  }}
                >
                  {/* Competency Name */}
                  <Box 
                    display="flex" 
                    alignItems="center" 
                    gap={1} 
                    mb={2}
                    sx={{
                      backgroundColor: "grey.200",
                      padding: 1.5,
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="h6" fontWeight={600}>
                      {subReq.name}
                    </Typography>
                    <Tooltip title="View Competencies">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenSubRequirementsModal(true);
                        }}
                        sx={{
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <Info fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {subReq.evaluation_metrics && subReq.evaluation_metrics.length > 0 && (
                      <Link
                        sx={{ 
                          cursor: "pointer", 
                          fontSize: "0.875rem",
                          textDecoration: "underline",
                          "&:hover": {
                            opacity: 0.8,
                          },
                        }}
                        onClick={() => {
                          setSelectedCompetencyForRubric(subReq);
                          setOpenSuccessRubric(true);
                        }}
                      >
                        See Success Rubric
                      </Link>
                    )}
                  </Box>
                  {subReq.description && (
                    <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                      {subReq.description}
                    </Typography>
                  )}

                  {/* Question 1: Comment */}
                  <Box mb={3}>
                    <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                      1. {RequestFeedbackSteps.question1Reflect}{" "}
                      <Typography component={"span"} color="error">
                        *
                      </Typography>
                    </Typography>
                    <TextField
                      id={`iter-comment-${subReq.requirement_id}`}
                      multiline
                      rows={4}
                      fullWidth
                      onChange={(e: BaseSyntheticEvent) => {
                        onUpdate({
                          type: ACTIONS.SET_ITER_SUB_REQUIREMENT_COMMENT,
                          payload: {
                            requirementId: subReq.requirement_id,
                            comment: e.target.value,
                          },
                        });
                      }}
                      value={data.iterSubRequirementComments[subReq.requirement_id] || ""}
                      error={!!commentError}
                      helperText={commentError}
                    />
                  </Box>

                  {/* Question 2: Rating */}
                  <Box id={`iter-rating-${subReq.requirement_id}`}>
                    <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                      2. {RequestFeedbackSteps.question2Reflect}{" "}
                      <Typography component={"span"} color="error">
                        *
                      </Typography>
                    </Typography>
                    <RateSelector
                      value={data.iterSubRequirementRates[subReq.requirement_id] || null}
                      onChange={(value: number) => {
                        onUpdate({
                          type: ACTIONS.SET_ITER_SUB_REQUIREMENT_RATE,
                          payload: {
                            requirementId: subReq.requirement_id,
                            rate: value,
                          },
                        });
                      }}
                      requirement={subReq}
                      customLabels={customLabels}
                    />
                    {ratingError && (
                      <Typography component="div" color="error" sx={{ mt: 1, fontSize: "0.875rem" }}>
                        {ratingError}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          // For non-ITER: Show questions separately
          <>
            <Box>
              <Typography variant="body1">
                1. {RequestFeedbackSteps.question1Reflect}{" "}
                <Typography component={"span"} color="error">
                  *
                </Typography>
              </Typography>
              <Box my={2}>
                <TextField
                  id="perform-text-field"
                  multiline
                  rows={4}
                  fullWidth
                  onChange={(e: BaseSyntheticEvent) => {
                    onUpdate({ type: ACTIONS.SET_PERFORM, payload: e.target.value });
                  }}
                  value={data.perform}
                  error={!!error.perform}
                  helperText={error.perform}
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="body1">
                2. {RequestFeedbackSteps.question2Reflect}{" "}
                <Typography component={"span"} color="error">
                  *
                </Typography>
              </Typography>
              <Box my={2} id="rate-selector">
                <RateSelector
                  value={data.rate as number}
                  onChange={(value: number) => {
                    onUpdate({ type: ACTIONS.SET_RATE, payload: value });
                  }}
                  requirement={data.selectedCompetencySkill}
                  customLabels={customLabels}
                />
                {!!error.rate && (
                  <Typography component="div" color="error" sx={{ mt: 1 }}>
                    {error.rate}
                  </Typography>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>

      <BackgroundTypography text="Coach Availability" />

      <Box
        sx={{
          p: coachAvailableActive ? 1.5 : 0,
          borderRadius: 2,
          outline: coachAvailableActive ? "2px solid" : "none",
          outlineColor: "primary.main",
          bgcolor: coachAvailableActive ? "action.hover" : "transparent",
        }}
      >
        <Typography variant="body1">
          {RequestFeedbackSteps.question3Reflect}
          <Typography component={"span"} color="error">
            *
          </Typography>
        </Typography>

        <FormControl sx={{ mt: 2 }} error={Boolean(error.coachAvailable)}>
          <RadioGroup
            aria-labelledby="coach-availability-label"
            name="coach-availability"
            value={data.coachAvailable ?? ""}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              onUpdate({
                type: ACTIONS.SET_COACH_AVAILABLE,
                payload: (e.target as HTMLInputElement).value === "true",
              });
            }}
          >
            <FormControlLabel value={true} control={<Radio />} label="Yes" />
            <FormControlLabel value={false} control={<Radio />} label="No" />
          </RadioGroup>
          {error.coachAvailable && (
            <Typography variant="caption" color="error">
              {error.coachAvailable}
            </Typography>
          )}
        </FormControl>
      </Box>

      {openSuccessRubric && (
        <>
          {isITER && selectedCompetencyForRubric ? (
            <SuccessRubric
              open={openSuccessRubric}
              onClose={() => {
                setOpenSuccessRubric(false);
                setSelectedCompetencyForRubric(null);
              }}
              requirement={selectedCompetencyForRubric}
              customLabels={customLabels}
            />
          ) : !isITER && data.selectedCompetencySkill ? (
            <SuccessRubric
              open={openSuccessRubric}
              onClose={() => setOpenSuccessRubric(false)}
              requirement={data.selectedCompetencySkill}
              customLabels={customLabels}
            />
          ) : null}
        </>
      )}

      {isITER && data.selectedCompetencySkill && (
        <SubRequirementsModal
          open={openSubRequirementsModal}
          onClose={() => setOpenSubRequirementsModal(false)}
          iterName={data.selectedCompetencySkill.name}
          subRequirements={subRequirements.map((subReq: any) => ({
            requirement_id: subReq.requirement_id,
            name: subReq.name,
            description: subReq.description || null,
          }))}
          ratingsByRequirement={buildRatingsByRequirementForIter}
        />
      )}
    </StepLayoutContainer>
  );
};
